import * as React from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export interface MultiSelectOption {
  value: string
  label: string
}

interface SearchableMultiSelectProps {
  /** Noun for the trigger/aria text, e.g. "Owner" → "All owners" / "Owner (3)". */
  label: string
  options: MultiSelectOption[]
  /** Currently selected option values. Empty array = "all" (no filter). */
  selected: string[]
  onChange: (next: string[]) => void
  searchPlaceholder?: string
  emptyText?: string
  triggerClassName?: string
  contentClassName?: string
  id?: string
}

/**
 * Accessible, searchable multi-select built on Command + Popover. Designed for
 * long option lists (type to filter). Empty selection means "all" (no filter);
 * checking items narrows to those. The menu stays open while selecting so several
 * items can be chosen in one go.
 */
export function SearchableMultiSelect({
  label,
  options,
  selected,
  onChange,
  searchPlaceholder,
  emptyText,
  triggerClassName,
  contentClassName,
  id,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const selectedSet = React.useMemo(() => new Set(selected), [selected])
  const labelLower = label.toLowerCase()

  const toggle = (value: string) =>
    onChange(selectedSet.has(value) ? selected.filter((v) => v !== value) : [...selected, value])
  const clear = () => onChange([])

  const triggerLabel =
    selected.length === 0 ? `All ${labelLower}s` : `${label} (${selected.length})`
  const listboxId = id ? `${id}-listbox` : undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-label={`Filter by ${labelLower}. ${
            selected.length === 0 ? `All ${labelLower}s shown.` : `${selected.length} selected.`
          }`}
          className={cn('w-44 justify-between font-normal', triggerClassName)}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-72 max-w-[min(24rem,calc(100vw-2rem))] p-0', contentClassName)}
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder ?? `Search ${labelLower}s...`} />
          <CommandList id={listboxId} role="listbox" aria-label={label} className="max-h-72">
            <CommandEmpty>{emptyText ?? `No ${labelLower}s found.`}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => {
                const isSelected = selectedSet.has(o.value)
                return (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => toggle(o.value)}
                    role="option"
                    aria-selected={isSelected}
                    className="items-start"
                  >
                    <span
                      className={cn(
                        'mr-2 mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                        isSelected
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-muted-foreground/40'
                      )}
                      aria-hidden="true"
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 whitespace-normal break-words">{o.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
          {selected.length > 0 && (
            <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
              <span className="text-muted-foreground" aria-live="polite">
                {selected.length} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2"
                onClick={clear}
                aria-label={`Clear selected ${labelLower}s`}
              >
                <X className="mr-1 h-3 w-3" aria-hidden="true" />
                Clear
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
